import React, { useState, useEffect } from 'react';
import { MainLayoutApple, CardApple, ButtonApple, ModalApple, InputApple } from '@/components/apple-ui';
import { Database, Search, Upload, Download, Trash2, Edit, Save, X, Loader2, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/apple-design-system.css';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TMPageApple = ({ user, planInfo, onLogout }) => {
  const { isDarkMode } = useTheme();
  const [tmEntries, setTmEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ source: '', target: '' });
  const [stats, setStats] = useState({ total: 0, thisMonth: 0 });
  const [catTmStats, setCatTmStats] = useState({ total_entries: 0 });

  useEffect(() => { fetchProjects(); fetchCatTmStats(); }, []);
  useEffect(() => { if (selectedProject) fetchTMEntries(); }, [selectedProject]);

  const fetchCatTmStats = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/cat/tm/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setCatTmStats(data); }
    } catch (error) { console.error('Failed to fetch CAT TM stats:', error); }
  };

  const handleClearCatTm = async () => {
    if (!window.confirm(`Clear all ${catTmStats.total_entries.toLocaleString()} CAT Translation Memory entries? This cannot be undone.`)) return;
    setClearing(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/cat/tm/clear`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Cleared ${data.deleted} TM entries`);
        setCatTmStats({ total_entries: 0 });
      } else { toast.error(data.detail || 'Failed to clear TM'); }
    } catch (error) { toast.error('Failed to clear TM'); }
    finally { setClearing(false); }
  };

  const fetchProjects = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data.projects) ? data.projects : []);
      setProjects(list);
      if (list.length > 0) setSelectedProject(list[0].project_id || list[0].id);
    } catch (error) { console.error('Failed to fetch projects:', error); }
  };

  const fetchTMEntries = async () => {
    if (!selectedProject) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Failed to load translation memory'); return; }
      const data = await res.json();
      const entries = data.cached_translations || data.tm_entries || [];
      setTmEntries(entries.map((e, idx) => ({
        id: e.id || idx,
        source: e.source_text || e.source,
        target: e.translated_text || e.target,
        created_at: e.created_at
      })));
      setStats({
        total: entries.length,
        thisMonth: entries.filter(e => {
          if (!e.created_at) return false;
          const date = new Date(e.created_at);
          const now = new Date();
          return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        }).length
      });
    } catch (error) {
      console.error('Failed to fetch TM entries:', error);
      setTmEntries([]);
    } finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.tmx')) { toast.error('Please upload a TMX file'); return; }
    if (!selectedProject) { toast.error('Please select a project first'); return; }

    setImporting(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}/tmx/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Imported ${data.stats?.imported || 0} entries, ${data.stats?.duplicates || 0} duplicates skipped`);
        fetchTMEntries();
      } else { toast.error(data.detail || 'Import failed'); }
    } catch (error) { toast.error('Failed to import TMX file'); }
    finally { setImporting(false); }
  };

  const handleExportTMX = async () => {
    if (!selectedProject) { toast.error('Please select a project first'); return; }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}/tmx/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tm_export_${selectedProject}.tmx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success('TMX file exported');
      } else { toast.error('Export failed'); }
    } catch (error) { toast.error('Failed to export TMX'); }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this TM entry?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}/tm/${entryId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { setTmEntries(prev => prev.filter(e => e.id !== entryId)); toast.success('Entry deleted'); }
      else { toast.error('Failed to delete entry'); }
    } catch (error) { toast.error('Failed to delete entry'); }
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProject}/tm/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        setTmEntries(prev => prev.map(e => e.id === editingId ? { ...e, ...editForm } : e));
        setEditingId(null);
        toast.success('Entry updated');
      } else { toast.error('Failed to update entry'); }
    } catch (error) { toast.error('Failed to update entry'); }
  };

  const filteredEntries = tmEntries.filter(entry => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return entry.source?.toLowerCase().includes(query) || entry.target?.toLowerCase().includes(query);
  });

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      <div className="apple-content" data-testid="apple-tm-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="apple-content-title flex items-center gap-3">
              <Database className="text-purple-600" size={28} />
              Translation Memory
            </h1>
            <p className="apple-content-subtitle">Manage your translation memory entries</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="apple-btn apple-btn-secondary cursor-pointer">
              <Upload size={16} className="mr-2" />
              {importing ? 'Importing...' : 'Import TMX'}
              <input type="file" accept=".tmx" onChange={handleFileUpload} className="hidden" disabled={importing || !selectedProject} data-testid="tmx-import-input" />
            </label>
            <ButtonApple variant="secondary" icon={Download} onClick={handleExportTMX} disabled={!selectedProject || tmEntries.length === 0} data-testid="export-tmx-btn">
              Export TMX
            </ButtonApple>
            <ButtonApple variant="danger" icon={Trash2} onClick={handleClearCatTm} disabled={clearing || catTmStats.total_entries === 0} data-testid="clear-cat-tm-btn">
              {clearing ? 'Clearing...' : 'Clear CAT TM'}
            </ButtonApple>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <CardApple className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-50"><Database size={20} className="text-purple-600" /></div>
              <div>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{catTmStats.total_entries.toLocaleString()}</p>
                <p className="text-xs text-gray-500">CAT TM Entries</p>
              </div>
            </div>
          </CardApple>
          <CardApple className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-50"><FileText size={20} className="text-blue-600" /></div>
              <div>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{stats.thisMonth.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Added This Month</p>
              </div>
            </div>
          </CardApple>
          <CardApple className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-50"><CheckCircle size={20} className="text-green-600" /></div>
              <div>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>FREE</p>
                <p className="text-xs text-gray-500">TM Match Savings</p>
              </div>
            </div>
          </CardApple>
        </div>

        {/* Filters */}
        <CardApple className="mb-6">
          <CardApple.Body>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 max-w-xs w-full">
                <label className="apple-label">Project</label>
                <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="apple-input apple-select" data-testid="tm-project-select">
                  <option value="">Select project...</option>
                  {projects.map(p => <option key={p.project_id || p.id} value={p.project_id || p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="flex-1 w-full">
                <label className="apple-label">Search</label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input type="text" placeholder="Search entries..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="apple-input pl-11" data-testid="tm-search-input" />
                </div>
              </div>
            </div>
          </CardApple.Body>
        </CardApple>

        {/* TM Entries Table */}
        <CardApple>
          <CardApple.Header>
            <CardApple.Title>TM Entries</CardApple.Title>
            <span className="text-sm text-gray-500">{filteredEntries.length} entries</span>
          </CardApple.Header>

          {loading ? (
            <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin text-purple-600" size={32} /></div>
          ) : !selectedProject ? (
            <div className="p-12 text-center">
              <Database className="mx-auto mb-4 text-gray-300" size={48} />
              <p className="text-gray-500">Select a project to view TM entries</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="p-12 text-center">
              <Database className="mx-auto mb-4 text-gray-300" size={48} />
              <p className="text-gray-500 mb-2">No TM entries found</p>
              <p className="text-sm text-gray-400">Import a TMX file or translate text to build your TM</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredEntries.map((entry, idx) => (
                <div key={entry.id || idx} className="p-5 hover:bg-gray-50 transition-colors" data-testid={`tm-entry-${entry.id || idx}`}>
                  {editingId === entry.id ? (
                    <div className="space-y-4">
                      <InputApple label="Source" value={editForm.source} onChange={(e) => setEditForm(prev => ({ ...prev, source: e.target.value }))} />
                      <InputApple label="Target" value={editForm.target} onChange={(e) => setEditForm(prev => ({ ...prev, target: e.target.value }))} />
                      <div className="flex items-center gap-2">
                        <ButtonApple size="sm" icon={Save} onClick={handleSaveEdit}>Save</ButtonApple>
                        <ButtonApple variant="ghost" size="sm" icon={X} onClick={() => setEditingId(null)}>Cancel</ButtonApple>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs font-medium text-gray-400 mb-1">Source</p>
                          <p className="text-sm text-gray-900">{entry.source}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-400 mb-1">Target</p>
                          <p className="text-sm text-gray-900">{entry.target}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditingId(entry.id); setEditForm({ source: entry.source, target: entry.target }); }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                          <Edit size={16} />
                        </button>
                        <button onClick={() => handleDeleteEntry(entry.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardApple>
      </div>
    </MainLayoutApple>
  );
};

export default TMPageApple;
